// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  Divider,
  CardMedia,
  Collapse,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";

// theme
import {
  B1Tiny,
  B3Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalAlertError,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Images Configuration
// ----------------------------------------------------------------------
const VALUE_RELATION_HELP_SECTIONS = {
  0: {
    image: "/illustrations/converted/indicator_combinations/value_off.png",
    description: (
      <>
        If the value condition is set to <b>Off</b>, this part of the indicator combination signal block <b>always evaluates as a positive signal</b>.
      </>
    ),
  },
  1: {
    image: "/illustrations/converted/indicator_combinations/value_1_equals_value_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the value line of{" "}
        <b>Input indicator 1 is precisely the same as that of Input indicator 2</b>.
      </>
    ),
  },
  2: {
    image: "/illustrations/converted/indicator_combinations/value_1_smaller_value_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the value line of{" "}
        <b>Input indicator 1 is smaller than the value line of Input indicator 2</b>.
      </>
    ),
  },
  3: {
    image: "/illustrations/converted/indicator_combinations/value_1_greater_value_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the value line of{" "}
        <b>Input indicator 1 is greater than the value line of Input indicator 2</b>.
      </>
    ),
  },
  4: {
    image: "/illustrations/converted/indicator_combinations/value_x_cross_value_x.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the value line of <b>Input indicator 1 and Input indicator 2 crosses each other</b>.
      </>
    ),
  },
  5: {
    image: "/illustrations/converted/indicator_combinations/value_1_cross_value_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the value line of <b>Input indicator 1 crosses the value line of Input indicator 2</b>.
      </>
    ),
  },
  6: {
    image: "/illustrations/converted/indicator_combinations/value_2_cross_value_1.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the value line of <b>Input indicator 2 crosses the value line of Input indicator 1</b>.
      </>
    ),
  },
};

const GRADIENT_RELATION_HELP_SECTIONS = {
  0: {
    image: "/illustrations/converted/indicator_combinations/gradient_off.png",
    description: (
      <>
        If the gradient condition is set to <b>Off</b>, this part of the indicator combination signal block{" "}
        <b>always evaluates as a positive signal</b>.
      </>
    ),
  },
  7: {
    image: "/illustrations/converted/indicator_combinations/gradient_1_equals_gradient_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the gradient line of{" "}
        <b>Input indicator 1 is precisely the same as that of Input indicator 2</b>.
      </>
    ),
  },
  8: {
    image: "/illustrations/converted/indicator_combinations/gradient_1_smaller_gradient_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the gradient line of{" "}
        <b>Input indicator 1 is smaller than the gradient line of Input indicator 2</b>.
      </>
    ),
  },
  9: {
    image: "/illustrations/converted/indicator_combinations/gradient_1_greater_gradient_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the gradient line of{" "}
        <b>Input indicator 1 is greater than the gradient line of Input indicator 2</b>.
      </>
    ),
  },
  10: {
    image: "/illustrations/converted/indicator_combinations/gradient_x_cross_gradient_x.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the gradient line of <b>Input indicator 1 and Input indicator 2 crosses each other</b>.
      </>
    ),
  },
  11: {
    image: "/illustrations/converted/indicator_combinations/gradient_1_cross_gradient_2.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the gradient line of <b>Input indicator 1 crosses the gradient line of Input indicator 2</b>
        .
      </>
    ),
  },
  12: {
    image: "/illustrations/converted/indicator_combinations/gradient_2_cross_gradient_1.png",
    description: (
      <>
        The relation is evaluated as a positive signal if the gradient line of <b>Input indicator 2 crosses the gradient line of Input indicator 1</b>
        .
      </>
    ),
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const IndicatorCombinationsDefaults = {
  nSignalInUse: true,
  nSignalUsedFor: "0",
  nInputInos1: "0",
  nInputInos2: "0",
  nRelation1: "0",
  nRelation2: "0",
  nSignalVisualization: false,
  nSignalAnalysisFeedbackState: true,
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const getIndicatorNameById = (indicators, indicatorId) => {
  if (!indicatorId || indicatorId === "") return "None selected";
  if (!indicators || indicators.length === 0) return "No indicators available";

  const indicator = indicators.find((ind) => ind.id.toString() === indicatorId.toString());
  return indicator ? `${indicator.name}` : `Unknown indicator (ID: ${indicatorId})`;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const getSwitchDisplay = (value) => (value === true ? "On" : "Off");

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const getSignalUsedForDisplay = (value) => {
  const usedForOptions = {
    0: "Entry",
    1: "Exit",
  };
  return usedForOptions[value] || "Entry";
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const IndicatorCombinationsBuildName = (config, indicators) =>
  `${getIndicatorNameById(indicators, config.nInputInos1)} \n combined with \n ${getIndicatorNameById(indicators, config.nInputInos2)}`;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const IndicatorCombinationsBuildSubtitle = (config) =>
  `${getSwitchDisplay(config.nSignalInUse)} | ${getSignalUsedForDisplay(config.nSignalUsedFor)}`;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function IndicatorCombinationsRenderParameters(selectedIndicatorCombination, getIndicatorCombinationConfig, professionalMode, indicators) {
  const config = getIndicatorCombinationConfig(selectedIndicatorCombination);

  if (!config) {
    return (
      <Typography variant="contentMiddle" align="center">
        No configuration saved yet!
      </Typography>
    );
  }

  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get relation 1 display
  const getRelation1Display = (value) => {
    const relations = {
      0: "Off",
      1: "Value 1 equals value 2",
      2: "Value 1 smaller value 2",
      3: "Value 1 greater value 2",
      4: "Value x cross value x",
      5: "Value 1 cross value 2",
      6: "Value 2 cross value 1",
    };
    return relations[value] || "Off";
  };

  // Helper function to get relation 2 display
  const getRelation2Display = (value) => {
    const relations = {
      0: "Off",
      7: "Gradient 1 equals gradient 2",
      8: "Gradient 1 smaller gradient 2",
      9: "Gradient 1 greater gradient 2",
      10: "Gradient x cross gradient x",
      11: "Gradient 1 cross gradient 2",
      12: "Gradient 2 cross gradient 1",
    };
    return relations[value] || "Off";
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Signal in use", getSwitchDisplay(config.nSignalInUse))}
      {renderParam("Signal used for", getSignalUsedForDisplay(config.nSignalUsedFor))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Input indicator 1", getIndicatorNameById(indicators, config.nInputInos1))}
      {renderParam("Input indicator 2", getIndicatorNameById(indicators, config.nInputInos2))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Value relation", getRelation1Display(config.nRelation1))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Gradient relation", getRelation2Display(config.nRelation2))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Signal visualization", getSwitchDisplay(config.nSignalVisualization))}
      {professionalMode === true ? <>{renderParam("Signal analysis feedback state", getSwitchDisplay(config.nSignalAnalysisFeedbackState))}</> : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const IndicatorCombinationsConfigure = forwardRef(({ onSave, initialData, professionalMode, indicators }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  IndicatorCombinationsConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
    indicators: PropTypes.array,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const [collapseValue, setCollapseValue] = useState(false);
  const [collapseGradient, setCollapseGradient] = useState(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Apply all the default values to the form
    Object.keys(IndicatorCombinationsDefaults).forEach((key) => {
      setFieldValue(key, IndicatorCombinationsDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nSignalInUse: initialData?.nSignalInUse ?? IndicatorCombinationsDefaults.nSignalInUse,
      nSignalUsedFor: initialData?.nSignalUsedFor ?? IndicatorCombinationsDefaults.nSignalUsedFor,
      nInputInos1: initialData?.nInputInos1 ?? IndicatorCombinationsDefaults.nInputInos1,
      nInputInos2: initialData?.nInputInos2 ?? IndicatorCombinationsDefaults.nInputInos2,
      nRelation1: initialData?.nRelation1 ?? IndicatorCombinationsDefaults.nRelation1,
      nRelation2: initialData?.nRelation2 ?? IndicatorCombinationsDefaults.nRelation2,
      nSignalVisualization: initialData?.nSignalVisualization ?? IndicatorCombinationsDefaults.nSignalVisualization,
      nSignalAnalysisFeedbackState: initialData?.nSignalAnalysisFeedbackState ?? IndicatorCombinationsDefaults.nSignalAnalysisFeedbackState,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          // Validate that both indicators are selected and different
          if (!values.nInputInos1 || !values.nInputInos2 || values.nInputInos1 === "0" || values.nInputInos2 === "0") {
            setError(true);
            setReturnMessage("Please select both input indicators.");
            setSubmitting(false);
            return;
          }
          if (values.nInputInos1 === values.nInputInos2) {
            setError(true);
            setReturnMessage("Input indicators must be different.");
            setSubmitting(false);
            return;
          }
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  // Check if there are at least 2 configured indicators available
  const indicatorsWithConfig = indicators.filter((indicator) => indicator.config !== null);
  const hasEnoughIndicators = indicatorsWithConfig.length >= 2;
  const showWarning = !hasEnoughIndicators;

  let saveAndCloseDisabled =
    !values.nInputInos1 ||
    !values.nInputInos2 ||
    values.nInputInos1 === "0" ||
    values.nInputInos2 === "0" ||
    values.nInputInos1 === values.nInputInos2;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure indicator combinations
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure indicator combinations</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {showWarning ? (
                    <Box>
                      <ModalAlertError
                        sx={{
                          m: "0 !important",
                        }}
                        severity="error"
                      >
                        At least two indicators must be configured before you can use indicator combinations.
                      </ModalAlertError>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalInUse */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal in use"
                        helpReference="HandleIndicatorCombinations"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalInUse"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalInUse}
                        onChange={(event) => {
                          setFieldValue("nSignalInUse", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalUsedFor */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal used for"
                        helpReference="HandleIndicatorCombinations"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalUsedFor"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nSignalUsedFor")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nSignalUsedFor && errors.nSignalUsedFor)}
                        >
                          <ModalContentMenuItem value="0">Entry</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Exit</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nInputInos1 */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Input indicator 1"
                      helpReference="HandleIndicatorCombinations"
                      helpText=""
                      helpLink=""
                      fieldName="nInputInos1"
                      touched={touched}
                      errors={errors}
                    />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nInputInos1")}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nInputInos1 && errors.nInputInos1)}
                      >
                        <ModalContentMenuItem value="0">
                          {hasEnoughIndicators ? "Please select indicator" : "Please configure at leats two indicators"}{" "}
                        </ModalContentMenuItem>
                        {indicators
                          .filter((indicator) => indicator.id.toString() !== values.nInputInos2 || values.nInputInos2 === "0")
                          .map((indicator) => (
                            <ModalContentMenuItem key={indicator.id} value={indicator.id.toString()}>
                              {indicator.name} ({indicator.subtitle})
                            </ModalContentMenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nInputInos2 */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Input indicator 2"
                      helpReference="HandleIndicatorCombinations"
                      helpText=""
                      helpLink=""
                      fieldName="nInputInos2"
                      touched={touched}
                      errors={errors}
                    />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nInputInos2")}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nInputInos2 && errors.nInputInos2)}
                      >
                        <ModalContentMenuItem value="0">
                          {hasEnoughIndicators ? "Please select indicator" : "Please configure at leats two indicators"}{" "}
                        </ModalContentMenuItem>
                        {indicators
                          .filter((indicator) => indicator.id.toString() !== values.nInputInos1 || values.nInputInos1 === "0")
                          .map((indicator) => (
                            <ModalContentMenuItem key={indicator.id} value={indicator.id.toString()}>
                              {indicator.name} ({indicator.subtitle})
                            </ModalContentMenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nRelation1 */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Value relation"
                      helpReference="HandleIndicatorCombinations"
                      helpText=""
                      helpLink=""
                      fieldName="nRelation1"
                      touched={touched}
                      errors={errors}
                    />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nRelation1")}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nRelation1 && errors.nRelation1)}
                      >
                        <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                        <ModalContentMenuItem value="1">Value 1 equals value 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="2">Value 1 smaller value 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="3">Value 1 greater value 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="4">Value x cross value x</ModalContentMenuItem>
                        <ModalContentMenuItem value="5">Value 1 cross value 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="6">Value 2 cross value 1</ModalContentMenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {VALUE_RELATION_HELP_SECTIONS[values.nRelation1] && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseValue(!collapseValue);
                        }}
                      >
                        {collapseValue ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseValue} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {VALUE_RELATION_HELP_SECTIONS[values.nRelation1].description}
                            <br />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={VALUE_RELATION_HELP_SECTIONS[values.nRelation1].image}
                            alt="Value relation help image"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "163px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nRelation2 */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Gradient relation"
                      helpReference="HandleIndicatorCombinations"
                      helpText=""
                      helpLink=""
                      fieldName="nRelation2"
                      touched={touched}
                      errors={errors}
                    />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nRelation2")}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nRelation2 && errors.nRelation2)}
                      >
                        <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                        <ModalContentMenuItem value="7">Gradient 1 equals gradient 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="8">Gradient 1 smaller gradient 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="9">Gradient 1 greater gradient 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="10">Gradient x cross gradient x</ModalContentMenuItem>
                        <ModalContentMenuItem value="11">Gradient 1 cross gradient 2</ModalContentMenuItem>
                        <ModalContentMenuItem value="12">Gradient 2 cross gradient 1</ModalContentMenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {GRADIENT_RELATION_HELP_SECTIONS[values.nRelation2] && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseGradient(!collapseGradient);
                        }}
                      >
                        {collapseGradient ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseGradient} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {GRADIENT_RELATION_HELP_SECTIONS[values.nRelation2].description}
                            <br />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={GRADIENT_RELATION_HELP_SECTIONS[values.nRelation2].image}
                            alt="Gradient relation help image"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "163px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalVisualization */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal visualization"
                        helpReference="HandleIndicatorCombinations"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalVisualization"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalVisualization}
                        onChange={(event) => {
                          setFieldValue("nSignalVisualization", event.target.checked);
                        }}
                      />
                    </Box>

                    {professionalMode === true ? (
                      <>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nSignalAnalysisFeedbackState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Signal analysis feedback state"
                            helpReference="HandleIndicatorCombinations"
                            helpText=""
                            helpLink=""
                            fieldName="nSignalAnalysisFeedbackState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nSignalAnalysisFeedbackState}
                            onChange={(event) => {
                              setFieldValue("nSignalAnalysisFeedbackState", event.target.checked);
                            }}
                          />
                        </Box>
                      </>
                    ) : null}
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
